import { Edgebanding, EdgeProfile, PartItem } from '@cutr/constants/cutlist';
import {
  EdgebandingSide,
  SMALL_PARTS_EDGEBANDING_FLAG,
} from '@cutr/constants/small-part-edgebanding';
import { PartOut, Sheet, SheetEdgeTrimConfig } from '@cutr/nesting';
import { useFlag } from '@unleash/proxy-client-react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { useActiveGroupGrainDirection } from '@/api/materialsGroup';
import { useCutlistState } from '@/api/store';
import { useCurrentFeatures } from '@/theme';
import { getManualEdges } from '@/utils/smallPartEdgebanding';

import styles from './NestingRenderer.module.css';

export const RenderSheet = ({
  size,
  sheet,
  sheetEdgeTrimConfig,
  wrapperWidth,
}: {
  sheet?: Sheet;
  size: number[];
  sheetEdgeTrimConfig: SheetEdgeTrimConfig | null;
  wrapperWidth: string;
}) => {
  const [w, h] = size;
  const ratio = h / w;
  const hasGrainDirection = useActiveGroupGrainDirection();

  return (
    <section
      className={cn(
        styles.nestingSheetWrapper,
        hasGrainDirection && styles.grainBackground
      )}
      style={{ width: wrapperWidth }}
    >
      <article
        className={styles.nestingSheet}
        style={{ '--paddingB': `calc(${ratio} * 100%)` } as React.CSSProperties}
      >
        {sheetEdgeTrimConfig && (
          <>
            {sheetEdgeTrimConfig.trimThickness.width1TrimThicknessMM > 0 && (
              <div
                style={{
                  width: '0px',
                  height: 'calc(100% + 20px)',
                  top: '-10px',
                  left: '0px',
                  borderWidth: '0.5px',
                  borderColor: 'var(--gray-7)',
                }}
              />
            )}
            {sheetEdgeTrimConfig.trimThickness.length2TrimThicknessMM > 0 && (
              <div
                style={{
                  width: 'calc(100% + 20px)',
                  height: '0px',
                  top: '0px',
                  left: '-10px',
                  borderWidth: '0.5px',
                  borderColor: 'var(--gray-7)',
                }}
              />
            )}
            {sheetEdgeTrimConfig.trimThickness.width2TrimThicknessMM > 0 && (
              <div
                style={{
                  width: '0px',
                  height: 'calc(100% + 20px)',
                  top: '-10px',
                  left: 'calc(100%)',
                  borderWidth: '0.5px',
                  borderColor: 'var(--gray-7)',
                }}
              />
            )}
            {sheetEdgeTrimConfig.trimThickness.length1TrimThicknessMM > 0 && (
              <div
                style={{
                  width: 'calc(100% + 20px)',
                  height: '0px',
                  top: 'calc(100%)',
                  left: '-10px',
                  borderWidth: '0.5px',
                  borderColor: 'var(--gray-7)',
                }}
              />
            )}
          </>
        )}
        {sheet?.parts?.map((part, idx) => (
          <RenderPart key={idx} part={part} size={size} />
        ))}
      </article>
    </section>
  );
};

type VisualSides = {
  top: boolean;
  bottom: boolean;
  left: boolean;
  right: boolean;
};

export const getNetPartBox = (
  part: Pick<PartOut, 'lengthMM' | 'widthMM' | 'orientation'>,
  net: Pick<PartItem, 'lengthMM' | 'widthMM'>,
  banded: VisualSides
) => {
  const isHorizontal = part.orientation === 'horizontal';
  const netX = isHorizontal ? net.lengthMM : net.widthMM;
  const netY = isHorizontal ? net.widthMM : net.lengthMM;
  if (part.lengthMM <= netX && part.widthMM <= netY) return null;

  const width = (Math.min(netX, part.lengthMM) * 100) / part.lengthMM;
  const height = (Math.min(netY, part.widthMM) * 100) / part.widthMM;
  return {
    width,
    height,
    left: banded.right && !banded.left ? 100 - width : 0,
    top: banded.bottom && !banded.top ? 100 - height : 0,
  };
};

const RenderPart = ({ part, size }: { part: PartOut; size: number[] }) => {
  const [w, h] = size;
  const { t } = useTranslation();
  const parts = useCutlistState.getState().parts;
  const { hasEdgeProfiling } = useCurrentFeatures();
  const isSmallPartEdgebandingEnabled = useFlag(SMALL_PARTS_EDGEBANDING_FLAG);

  const fullP = parts.find((p) => p.shortId === part.shortId);
  const manualEdges =
    isSmallPartEdgebandingEnabled && !hasEdgeProfiling && fullP
      ? getManualEdges(fullP)
      : [];

  const edges = {
    length1: false,
    length2: false,
    width1: false,
    width2: false,
  };

  const edgeGrooves = {
    length1: false,
    length2: false,
    width1: false,
    width2: false,
  };

  const sides: (keyof Edgebanding)[] | (keyof EdgeProfile)[] = [
    'length1',
    'length2',
    'width1',
    'width2',
  ];

  sides.map((side) => {
    const edge = hasEdgeProfiling
      ? fullP?.edgeProfile?.[side]
      : fullP?.edgebanding?.[side];

    if (edge) {
      edges[side] = true;
    }

    if (edge === 'none' || !edge) {
      edges[side] = false;
    }

    if (fullP?.grooves?.[side] && fullP?.grooves?.[side]?.position !== 'none') {
      edgeGrooves[side] = true;
    }
  });

  const width = (part.lengthMM * 100) / w;
  const height = (part.widthMM * 100) / h;

  const left = (part.offsetX * 100) / w;
  const top = (part.offsetY * 100) / h;

  const topBorder =
    part.orientation === 'horizontal' ? edges?.length2 : edges?.width2;
  const bottomBorder =
    part.orientation === 'horizontal' ? edges?.length1 : edges?.width1;
  const leftBorder =
    part.orientation === 'horizontal' ? edges?.width1 : edges?.length2;
  const rightBorder =
    part.orientation === 'horizontal' ? edges?.width2 : edges?.length1;

  const onWidth = '3px';
  const offWidth = '1px';

  const topBorderWidth = topBorder ? onWidth : offWidth;
  const bottomBorderWidth = bottomBorder ? onWidth : offWidth;
  const leftBorderWidth = leftBorder ? onWidth : offWidth;
  const rightBorderWidth = rightBorder ? onWidth : offWidth;

  const isHorizontal = part.orientation === 'horizontal';
  const borderColor = (isBanded: boolean, side: EdgebandingSide) => {
    if (!isBanded) return 'var(--gray-8)';
    return manualEdges.includes(side) ? 'var(--red-6)' : 'var(--primary)';
  };

  const topBorderColor = borderColor(
    topBorder,
    isHorizontal ? 'length2' : 'width2'
  );
  const bottomBorderColor = borderColor(
    bottomBorder,
    isHorizontal ? 'length1' : 'width1'
  );
  const leftBorderColor = borderColor(
    leftBorder,
    isHorizontal ? 'width1' : 'length2'
  );
  const rightBorderColor = borderColor(
    rightBorder,
    isHorizontal ? 'width2' : 'length1'
  );

  // Calculate which visual edges have grooves based on orientation
  const topGroove =
    part.orientation === 'horizontal'
      ? edgeGrooves.length2
      : edgeGrooves.width2;
  const bottomGroove =
    part.orientation === 'horizontal'
      ? edgeGrooves.length1
      : edgeGrooves.width1;
  const leftGroove =
    part.orientation === 'horizontal'
      ? edgeGrooves.width1
      : edgeGrooves.length2;
  const rightGroove =
    part.orientation === 'horizontal'
      ? edgeGrooves.width2
      : edgeGrooves.length1;

  const grooveSpacing = 4;
  const grooveWidth = 2;

  const netBox =
    fullP &&
    getNetPartBox(part, fullP, {
      top: topBorder,
      bottom: bottomBorder,
      left: leftBorder,
      right: rightBorder,
    });

  const placedSizeLabel = [part.lengthMM, part.widthMM].join('x');
  const sizeLabel =
    netBox && fullP
      ? [fullP.lengthMM, fullP.widthMM].join('x')
      : placedSizeLabel;
  const grossSizeLabel =
    part.orientation === 'horizontal'
      ? placedSizeLabel
      : [part.widthMM, part.lengthMM].join('x');
  const tooltip = [
    part.name,
    netBox
      ? t('cutlist-summary.nesting.presawPartSize', {
          netSize: sizeLabel,
          grossSize: grossSizeLabel,
        })
      : sizeLabel,
  ]
    .filter(Boolean)
    .join(', ');

  const placement = {
    width: `calc(${width}% - 1px)`, // give some space to ensure a gap between parts
    height: `calc(${height}% - 1px)`, // give some space to ensure a gap between parts
    top: `calc(${top}% + 0.5px)`,
    left: `calc(${left}% + 0.5px)`,
    position: 'absolute',
  } as const;

  const edgeStyle = {
    borderWidth: `${topBorderWidth} ${rightBorderWidth} ${bottomBorderWidth} ${leftBorderWidth}`,
    borderColor: `${topBorderColor} ${rightBorderColor} ${bottomBorderColor} ${leftBorderColor}`,
  };

  const content = (
    <>
      {/* Edge groove indicators */}
      {topGroove && (
        <div
          style={{
            position: 'absolute',
            top: `${grooveSpacing}px`,
            left: '0',
            right: '0',
            height: '2px',
            borderTop: `${grooveWidth}px solid var(--blue-7)`,
            pointerEvents: 'none',
          }}
        />
      )}
      {bottomGroove && (
        <div
          style={{
            position: 'absolute',
            bottom: `${grooveSpacing}px`,
            left: '0',
            right: '0',
            height: '2px',
            borderBottom: `${grooveWidth}px solid var(--blue-7)`,
            pointerEvents: 'none',
          }}
        />
      )}
      {leftGroove && (
        <div
          style={{
            position: 'absolute',
            left: `${grooveSpacing}px`,
            top: '0',
            bottom: '0',
            width: '2px',
            borderLeft: `${grooveWidth}px solid var(--blue-7)`,
            pointerEvents: 'none',
          }}
        />
      )}
      {rightGroove && (
        <div
          style={{
            position: 'absolute',
            right: `${grooveSpacing}px`,
            top: '0',
            bottom: '0',
            width: '2px',
            borderRight: `${grooveWidth}px solid var(--blue-7)`,
            pointerEvents: 'none',
          }}
        />
      )}
      <div>{part.name}</div>
      <div>{sizeLabel}</div>
    </>
  );

  if (netBox) {
    return (
      <div
        style={placement}
        className={cn('tooltip', styles.extraMaterial)}
        data-tooltip={tooltip}
      >
        <div
          className={styles.netPart}
          style={{
            ...edgeStyle,
            width: `${netBox.width}%`,
            height: `${netBox.height}%`,
            left: `${netBox.left}%`,
            top: `${netBox.top}%`,
          }}
        >
          {content}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{ ...placement, ...edgeStyle }}
      className="tooltip"
      data-tooltip={tooltip}
    >
      {content}
    </div>
  );
};
